import { useState, useEffect, useMemo, useCallback } from 'react';
import { parseTradeDeadline, tradeDeadlineLabel, DEADLINE_WEEK_OPTIONS, DEFAULT_DEADLINE_WEEK, type TradeDeadline } from '@/lib/nflTradeDeadline';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Navigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNFLLeague } from '@/contexts/NFLLeagueContext';
import { partitionSlots, slotsForBase } from '@/lib/nflRosterSlots';
import { leaguePositionLimits, visiblePositionLimits } from '@/lib/nflPositionLimits';
import {
  parseWaiverSettings, isFaabLocked, WAIVER_DEFAULTS,
} from '@/lib/nflWaiverSettings';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useLeagueRosterability } from '@/hooks/useStartablePositions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Settings, Users, ListOrdered, Calculator, Bomb, Gavel,
  DollarSign, ShieldAlert, Copy, Check, ChevronLeft,
  RotateCcw, Shuffle, Lock, Unlock,
  Megaphone, Send, Loader2, Trash2, LogOut, Bell, Trophy, ChevronUp, ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDraftTime } from '@/lib/draftTime';
import { useToast } from '@/hooks/use-toast';
import NFLScoringSettings from './NFLScoringSettings';
import { NFLBonusesSection } from './bonuses/NFLBonusesSection';
import { LINEUP_REMINDER_TOGGLE_DESC } from '@/lib/nflLineupReminder';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface MemberRow {
  user_id: string;
  role: string;
  username: string | null;
  team_name: string | null;
  /** SAB-1004: the member's team, so a commissioner can reset its logo from here. */
  team_id: string | null;
  team_logo_url: string | null;
}

type SettingsTab =
  | 'menu'
  | 'league'
  | 'members'
  | 'roster'
  | 'scoring'
  | 'sabotages'
  | 'draft'
  | 'funds'
  | 'bonuses'
  | 'notifications'
  | 'commissioner';

/* ------------------------------------------------------------------ */
/*  Menu items                                                         */
/* ------------------------------------------------------------------ */

const MENU_ITEMS: { id: SettingsTab; label: string; icon: typeof Settings; commishOnly?: boolean }[] = [
  { id: 'league', label: 'League', icon: Settings },
  { id: 'members', label: 'Members', icon: Users },
  { id: 'roster', label: 'Roster', icon: ListOrdered },
  { id: 'scoring', label: 'Scoring', icon: Calculator },
  { id: 'sabotages', label: 'Sabotages', icon: Bomb },
  { id: 'draft', label: 'Draft', icon: Gavel },
  { id: 'funds', label: 'Wallet', icon: DollarSign },
  { id: 'bonuses', label: 'Bonuses & Survivor', icon: Trophy },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'commissioner', label: 'Commissioner Tools', icon: ShieldAlert, commishOnly: true },
];

/**
 * SAB-886: Roster Settings shows the LEAGUE's slots. It previously showed three hardcoded
 * lists, which is how a commissioner could set IDP to 0 and still be told their starting
 * lineup contained IDP1 and IDP2 -- the screen whose entire job is to report their own
 * configuration back to them. Derived per-render from currentLeague.settings below.
 */

/* ------------------------------------------------------------------ */
/*  Commissioner Card Customization                                    */
/* ------------------------------------------------------------------ */

function CommissionerCardCustomization({
  leagueId,
  leagueData,
  setLeagueData,
}: {
  leagueId: string;
  leagueData: any;
  setLeagueData: (d: any) => void;
}) {
  const { toast } = useToast();
  const [cards, setCards] = useState<any[]>([]);
  const [enabledCards, setEnabledCards] = useState<string[] | null>(null); // null = all
  const [cardCosts, setCardCosts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data } = await supabase
        .from('nfl_sabotage_cards')
        .select('id, name, emoji, rarity, cost, category, is_active')
        .eq('is_active', true)
        .order('rarity')
        .order('category')
        .order('name');

      if (cancelled || !data) return;
      setCards(data);

      const settings = leagueData?.settings || {};
      const saved = settings.enabled_cards || null;
      const costs = settings.card_costs || {};

      setEnabledCards(saved ? [...saved] : null);
      setCardCosts({ ...costs });
      setLoaded(true);
    }
    load();
    return () => { cancelled = true; };
  }, [leagueData]);

  const isCardEnabled = (cardId: string) =>
    enabledCards === null || enabledCards.includes(cardId);

  const toggleCard = (cardId: string) => {
    if (enabledCards === null) {
      // Switching from "all" to explicit list — include everything except this one
      const allIds = cards.map(c => c.id).filter(id => id !== cardId);
      setEnabledCards(allIds);
    } else if (enabledCards.includes(cardId)) {
      setEnabledCards(enabledCards.filter(id => id !== cardId));
    } else {
      const next = [...enabledCards, cardId];
      // If all cards are now enabled, switch back to null (all)
      if (next.length === cards.length) {
        setEnabledCards(null);
      } else {
        setEnabledCards(next);
      }
    }
  };

  const setCostForCard = (cardId: string, cost: number) => {
    const defaultCost = cards.find(c => c.id === cardId)?.cost ?? 10;
    if (cost === defaultCost) {
      const next = { ...cardCosts };
      delete next[cardId];
      setCardCosts(next);
    } else {
      setCardCosts({ ...cardCosts, [cardId]: cost });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const currentSettings = leagueData?.settings || {};
      const newSettings = {
        ...currentSettings,
        enabled_cards: enabledCards,
        card_costs: Object.keys(cardCosts).length > 0 ? cardCosts : undefined,
      };

      // Clean undefined
      if (!newSettings.card_costs) delete newSettings.card_costs;
      if (!newSettings.enabled_cards) delete newSettings.enabled_cards;

      const { error } = await supabase
        .from('nfl_leagues')
        .update({ settings: newSettings })
        .eq('id', leagueId);

      if (error) {
        toast({ title: 'Error', description: error.message, variant: 'destructive' });
      } else {
        setLeagueData({ ...leagueData, settings: newSettings });
        toast({ title: 'Saved', description: 'Card settings updated' });
      }
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  const RARITY_ORDER = ['common', 'rare', 'legendary'];
  const grouped = RARITY_ORDER.map(r => ({
    rarity: r,
    cards: cards.filter(c => c.rarity === r),
  })).filter(g => g.cards.length > 0);

  const enabledCount = enabledCards === null ? cards.length : enabledCards.length;

  return (
    <>
      <Card className="bg-card border-border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-foreground flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Bomb className="h-4 w-4 text-primary" />
              Card Customization
            </span>
            <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
              {enabledCount}/{cards.length} enabled
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0 space-y-3">
          {grouped.map(({ rarity, cards: rCards }) => (
            <div key={rarity}>
              <p className={cn(
                'text-[10px] font-bold uppercase tracking-wider mb-1.5',
                rarity === 'legendary' ? 'text-amber-400'
                  : rarity === 'rare' ? 'text-blue-400'
                  : 'text-muted-foreground'
              )}>
                {rarity} ({rCards.filter(c => isCardEnabled(c.id)).length}/{rCards.length})
              </p>
              <div className="space-y-0.5">
                {rCards.map(card => {
                  const enabled = isCardEnabled(card.id);
                  const customCost = cardCosts[card.id];
                  const displayCost = customCost ?? card.cost;
                  return (
                    <div
                      key={card.id}
                      className={cn(
                        'flex items-center gap-2 py-1.5 px-2 rounded transition-colors',
                        enabled ? 'bg-transparent' : 'bg-muted/20 opacity-50'
                      )}
                    >
                      <button
                        onClick={() => toggleCard(card.id)}
                        className="shrink-0"
                      >
                        {enabled ? (
                          <Unlock className="h-3.5 w-3.5 text-emerald-400" />
                        ) : (
                          <Lock className="h-3.5 w-3.5 text-red-400" />
                        )}
                      </button>
                      <span className="text-sm flex-1 truncate">
                        {card.emoji} {card.name}
                      </span>
                      <Input
                        type="number"
                        min={0}
                        max={999}
                        value={displayCost}
                        onChange={(e) => setCostForCard(card.id, Number(e.target.value) || 0)}
                        className="w-16 h-6 text-[10px] text-center bg-muted/30 border-border/50 px-1"
                      />
                      <span className="text-[9px] text-muted-foreground w-4">SP</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-primary hover:bg-primary/90 mt-2"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save Card Settings
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Position Eligibility — RENDERED FROM SOURCE, never retyped         */
/* ------------------------------------------------------------------ */
/**
 * SAB-739. This card used to be six hardcoded <p> lines, and they were wrong:
 * they claimed "QB → QB, FLEX" when no code anywhere allows a QB in FLEX (both
 * the draft RPC and nfl-lineup's canPlaySlot say FLEX is RB/WR/TE), and they
 * listed only "DB / LB / DL" for IDP while CB, DE, DT, FS, MLB, NT, OLB and SAF
 * are all IDP-eligible. Documentation that contradicts behaviour is the same
 * defect class as the "21+" and "identity-verified at signup" copy (SAB-722).
 *
 * It now reads public.nfl_slot_eligibility — the same rows nfl_acquire_pick_lock
 * uses to assign slots — so it cannot drift from the rules it describes. If a
 * position is added to the map, this card shows it with no code change.
 */
/**
 * SAB-782: the playoff-seeding tiebreaker, per Adam's ruling of 2026-08-28.
 *
 * Two options only. "Divisional record" was specified but is NOT offered, because no division
 * concept exists on either sport -- no column, no table, no settings key (SAB-913). A third
 * dropdown entry with nothing behind it is SAB-903 shipped a second time while fixing it.
 *
 * LOCKED AT DRAFT COMPLETION, and the lock is real rather than cosmetic: SAB-907's
 * nfl_leagues_guard_cols freezes `settings` at draft_status='completed' and rejects the write
 * with 42501. This renders read-only past that point rather than offering a control that
 * cannot work -- the SAB-790 lesson, and the same reason SAB-909 exists for the card panel.
 *
 * Contest Rules section 8 discloses exactly this, including the fall-through, so the copy here
 * must not drift from it.
 */
function CommissionerTiebreaker({ leagueId, leagueData, setLeagueData, isCommissioner }: {
  leagueId: string; leagueData: any; setLeagueData: (f: any) => void; isCommissioner: boolean;
}) {
  const { toast } = useToast();
  const [saving, setSaving] = useState<string | null>(null);

  // SAB-1074: members render this card read-only. The tiebreaker decides standings order
  // and therefore playoff seeding, so it is a rule a member needs to be able to read --
  // it was previously behind `{isCommissioner && ...}` and invisible to everyone else.
  // `locked` now covers both "frozen for everyone" and "not yours to change".
  const locked = leagueData?.draft_status === 'completed' || !isCommissioner;
  const current: 'total_points' | 'head_to_head' =
    leagueData?.settings?.playoff_tiebreaker === 'head_to_head' ? 'head_to_head' : 'total_points';

  const OPTIONS = [
    { key: 'total_points' as const, label: 'Total points scored',
      blurb: 'Tied teams are ranked by total points scored across the season.' },
    { key: 'head_to_head' as const, label: 'Head-to-head record',
      blurb: 'Wins minus losses in games between the tied teams. If they never met or are level, total points decides.' },
  ];

  const choose = async (key: 'total_points' | 'head_to_head') => {
    if (locked || key === current) return;
    setSaving(key);
    // Targeted key write, never a wholesale settings replace: a full-object PATCH drops the
    // draft pointer, deadline and pause markers at once.
    const next = { ...(leagueData?.settings || {}), playoff_tiebreaker: key };
    // `supabase as any`: the generated types are stale and do not carry nfl_leagues, so a
    // typed .update() scores two errors on the CI ratchet. Same idiom as the sibling
    // card-customization write and NFLDraftSabotagePanel.
    const { error } = await (supabase as any)
      .from('nfl_leagues').update({ settings: next }).eq('id', leagueId);
    setSaving(null);
    if (error) {
      toast({ title: 'Could not save', description: error.message, variant: 'destructive' });
      return;
    }
    setLeagueData((d: any) => (d ? { ...d, settings: next } : d));
    toast({ title: 'Tiebreaker updated', description: OPTIONS.find(o => o.key === key)!.label });
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary" />
          Playoff Seeding Tiebreaker
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-2">
        <p className="text-[10px] text-muted-foreground">
          Teams are ranked by wins. This decides ties on wins.
        </p>
        {OPTIONS.map(o => {
          const active = o.key === current;
          return (
            <button
              key={o.key}
              onClick={() => choose(o.key)}
              disabled={locked || saving !== null}
              data-tiebreaker-option={o.key}
              data-active={active}
              className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${
                active ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted'
              } ${locked ? 'opacity-70 cursor-default' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">{o.label}</span>
                {saving === o.key
                  ? <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                  : active && <Check className="h-3.5 w-3.5 text-primary" />}
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">{o.blurb}</p>
            </button>
          );
        })}
        <p className="text-[10px] text-muted-foreground">
          {locked
            ? 'Fixed when the draft completed. It cannot be changed for this league.'
            : 'Can be changed until the draft completes, then it is fixed for the season.'}
        </p>
      </CardContent>
    </Card>
  );
}

/**
 * SAB-917: the commissioner's DRAFT ORDER EDITOR.
 *
 * Replaces a read-only card. Before this, a commissioner could randomize the order or
 * accept whatever initialization produced -- there was no way to place a specific team in
 * a specific slot, on either sport. Live draft lotteries, keeper penalties and
 * reverse-standings order are all normal practice.
 *
 * ── WHY THE WRITE GOES THROUGH AN RPC AND NOT A CLIENT LOOP ─────────────────
 *
 * nfl_teams' UPDATE policy is OWNERSHIP-ONLY -- `USING (owner_id = auth.uid())`. It is not
 * member-scoped; that is the SELECT policy (SAB-897). So a commissioner cannot write any
 * team row but their own, and a policy-filtered UPDATE returns success with ZERO ROWS
 * rather than an error. A client-side loop therefore cannot do this at all, and would fail
 * silently if it tried. nfl_set_draft_order is SECURITY DEFINER for exactly that reason,
 * reassigns 1..N in ONE statement, and asserts the result is a complete permutation before
 * returning -- a partial reassignment leaves duplicate or missing positions that
 * nfl-initialize-draft would then build a pick order from without complaint.
 *
 * ── THE LOCK POINT IS INITIALIZATION, NOT 'completed' ───────────────────────
 *
 * After init there are two independent representations of draft order: the materialised
 * nfl_draft_picks rows, and the live re-read of nfl_teams ORDER BY draft_position that
 * nfl-make-pick feeds to computeRoundOrder for sabotage effects. Editing positions after
 * init desyncs them, so the two halves of the draft disagree about who picks when. The
 * editor is therefore disabled once draft_status leaves 'pending', and the RPC enforces
 * the same thing server-side -- this control being hidden is not the guarantee.
 */
export function CommissionerDraftOrder({ leagueId, teams, myTeamId, draftStatus }: {
  leagueId: string;
  teams: { id: string; name: string; draft_position: number | null }[];
  myTeamId: string | null;
  draftStatus: string;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [saving, setSaving] = useState(false);

  // Stored order, by the field that means it. SAB-918: never by array index, and with a
  // deterministic secondary so a partially-assigned league is stable rather than arbitrary.
  const stored = useMemo(() => [...teams].sort((a, b) => {
    const ap = a.draft_position, bp = b.draft_position;
    if (ap != null && bp != null && ap !== bp) return ap - bp;
    if (ap != null && bp == null) return -1;
    if (ap == null && bp != null) return 1;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  }), [teams]);

  const [order, setOrder] = useState<string[]>(() => stored.map(t => t.id));
  useEffect(() => { setOrder(stored.map(t => t.id)); }, [stored]);

  const locked = draftStatus !== 'pending';
  const byId = useMemo(() => new Map(teams.map(t => [t.id, t])), [teams]);
  const dirty = order.some((id, i) => stored[i]?.id !== id);

  const move = (from: number, to: number) => {
    if (locked || to < 0 || to >= order.length) return;
    const next = [...order];
    [next[from], next[to]] = [next[to], next[from]];
    setOrder(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data, error } = await (supabase as any)
        .rpc('nfl_set_draft_order', { p_league_id: leagueId, p_team_ids: order });
      if (error) throw new Error(error.message);
      // The RPC returns a result object rather than throwing for expected refusals, so a
      // non-throwing call is NOT proof it applied. Read ok before claiming success.
      if (!data?.ok) {
        toast({
          title: 'Could not save the draft order',
          description: data?.code === 'DRAFT_STARTED'
            ? 'The draft has already started, so the order is fixed.'
            : data?.code === 'COMMISSIONER_ONLY'
              ? 'Only the commissioner can change the draft order.'
              : `${data?.code ?? 'Unknown error'}`,
          variant: 'destructive',
        });
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['nfl-teams', leagueId] });
      toast({ title: 'Draft order saved', description: `${data.teams} teams · ${data.rows_changed} changed` });
    } catch (err) {
      toast({ title: 'Error', description: String(err), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border" data-testid="draft-order-editor">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground flex items-center justify-between">
          <span>Draft Order</span>
          {dirty && !locked && (
            <Button size="sm" onClick={save} disabled={saving} data-testid="draft-order-save" className="h-7 text-xs">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save order'}
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-1">
        {order.map((id, i) => {
          const t = byId.get(id);
          if (!t) return null;
          return (
            <div
              key={id}
              data-testid="draft-order-row"
              data-team-id={id}
              className={cn('flex items-center gap-2 py-1.5 px-2 rounded text-sm',
                id === myTeamId && 'bg-primary/5')}
            >
              {/* SAB-918: the slot the team will actually draft in. While unsaved edits are
                  pending this is the row's NEW position, which is what the commissioner is
                  choosing; the stored value returns on save or on discard. */}
              <span className="w-5 text-xs text-muted-foreground text-center" data-testid="draft-order-slot">
                {i + 1}
              </span>
              <span className={cn('flex-1 text-foreground', id === myTeamId && 'text-primary')}>
                {t.name}
              </span>
              {!locked && (
                <span className="flex items-center gap-0.5">
                  <button
                    type="button" aria-label={`Move ${t.name} up`} data-testid="draft-order-up"
                    onClick={() => move(i, i - 1)} disabled={i === 0 || saving}
                    className="h-7 w-7 grid place-items-center rounded hover:bg-muted disabled:opacity-30"
                  ><ChevronUp className="h-3.5 w-3.5" /></button>
                  <button
                    type="button" aria-label={`Move ${t.name} down`} data-testid="draft-order-down"
                    onClick={() => move(i, i + 1)} disabled={i === order.length - 1 || saving}
                    className="h-7 w-7 grid place-items-center rounded hover:bg-muted disabled:opacity-30"
                  ><ChevronDown className="h-3.5 w-3.5" /></button>
                </span>
              )}
            </div>
          );
        })}
        <p className="text-[10px] text-muted-foreground pt-1">
          {locked
            ? 'The draft has started, so the order is fixed.'
            : 'Set the order before the draft starts. It cannot be changed once the draft begins.'}
        </p>
      </CardContent>
    </Card>
  );
}

function PositionEligibilityCard({ rosterSlots }: { rosterSlots: unknown }) {
  const [rows, setRows] = useState<{ position: string; slot_base: string; priority: number }[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('nfl_slot_eligibility' as any)
        .select('position, slot_base, priority')
        .eq('is_alias', false)   // hide tolerated spellings (S/SS/HC) no player uses
        .order('priority', { ascending: true });
      if (cancelled) return;
      if (error || !data) { setFailed(true); return; }
      setRows(data as any);
    })();
    return () => { cancelled = true; };
  }, []);

  // Group by the slot set so positions sharing eligibility collapse onto one
  // line (all the IDP positions, K/P/DST/COACH), matching how a human reads it.
  const grouped = useMemo(() => {
    if (!rows) return [];
    // SAB-886/SAB-951: expand against THIS LEAGUE's slots, not four hardcoded cases. The
    // old version was the same RB/WR/IDP/else shape SAB-951 deleted from the pick writer,
    // with the same two failures: it claimed IDP1/IDP2 in leagues with no IDP, and it
    // could never name FLEX2 because there was no FLEX case. An empty result is real --
    // it means the position has no starting slot here -- and is rendered as such below.
    const expand = (base: string) => slotsForBase(base, rosterSlots);
    const byPos = new Map<string, string[]>();
    for (const r of [...rows].sort((a, b) => a.priority - b.priority)) {
      byPos.set(r.position, [...(byPos.get(r.position) ?? []), ...expand(r.slot_base)]);
    }
    const bySlots = new Map<string, string[]>();
    for (const [pos, slots] of byPos) {
      const key = slots.join(',');
      bySlots.set(key, [...(bySlots.get(key) ?? []), pos]);
    }
    return [...bySlots.entries()].map(([slots, positions]) => ({
      positions: positions.sort().join(' / '),
      // `[].join(',')` is '' and `''.split(',')` is [''], which would render a stray
      // leading comma. An empty list is a real answer here, not a formatting accident.
      slots: slots.split(',').filter(Boolean),
    }));
  }, [rows, rosterSlots]);

  // The reserve slots this league actually has. A league with no IR must not be told it
  // has one, which is the same class of untruth as the IDP rows this ticket removes.
  const reserve = useMemo(() => {
    const p = partitionSlots(rosterSlots);
    return [...(p.bench.length ? ['BN'] : []), ...(p.ir.length ? ['IR'] : [])];
  }, [rosterSlots]);

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Position Eligibility</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 text-xs text-muted-foreground space-y-1">
        {failed && <p>Eligibility rules are unavailable right now.</p>}
        {!failed && !rows && <p>Loading…</p>}
        {grouped.map(g => (
          <p key={g.positions}>
            <span className="text-foreground">{g.positions}</span>
            {' → '}
            {[...g.slots, ...reserve].join(', ') || 'no slot in this league'}
          </p>
        ))}
        {rows && (
          <p className="pt-1 text-[10px] text-muted-foreground/70">
            {reserve.length > 0
              ? `Every position may also occupy a ${reserve.includes('BN') ? 'bench (BN)' : ''}${reserve.length === 2 ? ' or ' : ''}${reserve.includes('IR') ? 'injured-reserve (IR)' : ''} slot.`
              : 'This league has no bench or injured-reserve slots.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * SAB-989: the commissioner's positional roster caps.
 *
 * "Max 2 QB" is a thing slot eligibility cannot express. Eligibility says WHERE a player may
 * sit; bench slots take anyone, so nothing stopped a team drafting five quarterbacks.
 *
 * ── THIS CARD IS NOT THE CONTROL ────────────────────────────────────────────
 *
 * guard_nfl_position_limit (a BEFORE INSERT trigger on nfl_rosters) refuses the write
 * whatever any client believes, and the auto-pick selectors skip capped positions so the
 * cap cannot wedge a draft. This card only lets a human express the rule; the validation
 * below is a courtesy that catches the mistake before it is saved, not a boundary.
 *
 * ── THE ONE MISTAKE WORTH REFUSING ──────────────────────────────────────────
 *
 * A cap below the number of starter slots only that position can fill makes the roster
 * impossible to complete: cap QB at 0 in a league with a QB slot and no team can ever field
 * a legal lineup, while auto-pick quietly fills the slot with nobody. That minimum is
 * DERIVED from this league's own slots and the eligibility table -- not a constant -- for
 * the same reason SAB-951 deleted the hardcoded slot expansions: a constant is wrong for
 * any league that customised its roster.
 *
 * Blank means no limit. Zero is a real cap and bans the position, which is the only way to
 * say "no kickers" -- so it is allowed exactly when the position has no dedicated slot.
 */
/**
 * SAB-1020 — the commissioner's Waivers card.
 *
 * Writes through nfl_set_waiver_settings, the SAB-989-pattern targeted jsonb op. NOT a
 * settings PATCH: settings also carries draft_current_pick and draft_pick_deadline, which
 * move on every pick, and a commissioner who opens this page mid-draft and saves two picks
 * later would otherwise write the pick pointer back as it was on load — rewinding the draft.
 *
 * THIS CARD IS NOT THE CONTROL. The RPC re-asserts the commissioner check (it is SECURITY
 * DEFINER, so without that it would be a way around nfl_leagues RLS rather than a wrapper
 * over it) and enforces the FAAB lock. Everything here is affordance: showing a disabled
 * input with a reason beats showing a live one that throws on submit.
 */
/**
 * SAB-1058 — the commissioner's Trade Deadline card.
 *
 * Writes through nfl_set_trade_deadline, the SAB-989/1020 targeted jsonb op. NOT a settings
 * PATCH: a wholesale write loses every key set between this page's read and its save.
 *
 * The card stores a WEEK and displays a DATE it did not compute. nfl_trade_deadline()
 * derives the moment from the schedule on every read, so what the commissioner sees here is
 * the same value nfl-trade refuses on. A client that worked out "first game of week N+1"
 * itself would be a second derivation free to drift from the refusal -- which is the defect
 * SAB-1035 removed and SAB-1045 had to remove again.
 */
/* ------------------------------------------------------------------ */
/*  Playoff Configuration (SAB-1075)                                   */
/* ------------------------------------------------------------------ */

/**
 * SAB-1075. Playoff structure over nfl_leagues.playoff_start_week / .playoff_teams --
 * columns that have always existed and that generate-nfl-schedule already reads. What was
 * missing was any way to set them, and any way for a MEMBER to read them (SAB-1074).
 *
 * Every value here comes from the nfl_playoff_config RPC, including the derived
 * championship week and the list of legal team counts. The client does not re-derive
 * either. That is deliberate: the bracket generator builds 4- and 6-team shapes only, in
 * hardcoded branches, and a client that decided for itself which counts were offerable
 * would be free to drift from what the engine can actually build -- offering a commissioner
 * a bracket that silently generates nothing on playoff week.
 *
 * Read-only for members and for anyone once the league locks, editable for a commissioner
 * before that: same card, gated affordances, one derivation feeding both.
 */
export function PlayoffConfigCard({
  leagueId, isCommissioner, onSaved,
}: {
  leagueId: string | undefined;
  isCommissioner: boolean;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const [cfg, setCfg] = useState<any>(null);
  const [startWeek, setStartWeek] = useState<string>('15');
  const [teams, setTeams] = useState<string>('4');
  const [deadlineWeek, setDeadlineWeek] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId) return;
    const { data } = await (supabase as any).rpc('nfl_playoff_config', { p_league_id: leagueId });
    if (!data) return;
    setCfg(data);
    setStartWeek(String(data.playoff_start_week ?? 15));
    setTeams(String(data.playoff_teams ?? 4));
    // The deadline comes from ITS OWN shipped derivation (SAB-1058), never recomputed
    // here. This card only compares the two numbers; it does not decide what either is.
    const { data: dl } = await (supabase as any).rpc('nfl_trade_deadline', { p_league_id: leagueId });
    const parsed = parseTradeDeadline(dl);
    setDeadlineWeek(parsed.enabled ? (parsed.week ?? null) : null);
  }, [leagueId]);

  useEffect(() => { void load(); }, [load]);

  const locked = !!cfg?.locked;
  const canEdit = isCommissioner && !locked && !saving;

  // Rounds per supported shape, read from the RPC rather than assumed, so the preview
  // cannot disagree with the bracket the engine will build.
  const roundsFor = (n: number) => (n === 4 ? 2 : n === 6 ? 3 : null);
  const previewChampWeek = (() => {
    const r = roundsFor(Number(teams));
    return r ? Number(startWeek) + r - 1 : null;
  })();

  const supported: number[] = Array.isArray(cfg?.supported_team_counts)
    ? cfg.supported_team_counts.map(Number)
    : [4, 6];
  const maxWeek: number = Number(cfg?.max_week ?? 18);

  // Only offer start weeks whose championship still lands inside the season for the
  // CURRENTLY SELECTED bracket size -- a 6-team bracket needs one more week than a
  // 4-team one, so the legal range narrows as the size grows.
  const startOptions = (() => {
    const r = roundsFor(Number(teams));
    if (!r) return [];
    const out: number[] = [];
    for (let w = 2; w + r - 1 <= maxWeek; w++) if (w >= 10) out.push(w);
    return out;
  })();

  // SOFT warning, never a block. Adam's ruling on SAB-1058 stands: the deadline and the
  // playoff start are independent settings and a commissioner may set them however they
  // like. A deadline at or after the playoff start is usually a mistake, so it is said
  // out loud -- and then allowed.
  const deadlineAfterPlayoffs =
    deadlineWeek != null && Number(startWeek) > 0 && deadlineWeek >= Number(startWeek);

  const save = async () => {
    if (!leagueId) return;
    setSaving(true);
    try {
      const { error } = await (supabase as any).rpc('nfl_set_playoff_config', {
        p_league_id: leagueId,
        p_patch: { playoff_start_week: Number(startWeek), playoff_teams: Number(teams) },
      });
      if (error) throw error;
      await load();
      onSaved?.();
      toast({ title: 'Saved', description: 'Playoff settings updated.' });
    } catch (e: any) {
      // The RPC raises sentences, not codes -- surface them. A commissioner who picked a
      // week that pushes the final past week 18 should be told exactly that.
      toast({
        title: 'Not saved',
        description: e?.message ?? 'Could not save playoff settings.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Playoffs</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!cfg ? (
          <p className="text-[11px] text-muted-foreground">Loading…</p>
        ) : (
          <>
            {/* The summary line every member reads, derived server-side. */}
            <p className="text-xs text-foreground">
              Playoffs begin <span className="font-medium">Week {cfg.playoff_start_week}</span>
              {' · '}<span className="font-medium">{cfg.playoff_teams} teams</span>
              {cfg.championship_week ? <> {' · '}Championship <span className="font-medium">Week {cfg.championship_week}</span></> : null}
            </p>
            <p className="text-[11px] text-muted-foreground">
              Regular season runs Weeks 1–{cfg.regular_season_weeks}.
            </p>

            {isCommissioner && !locked && (
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-muted-foreground">Start</label>
                <Select value={startWeek} onValueChange={setStartWeek} disabled={!canEdit}>
                  <SelectTrigger className="h-8 w-28 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {startOptions.map(w => (
                      <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="text-xs text-muted-foreground">Teams</label>
                <Select value={teams} onValueChange={setTeams} disabled={!canEdit}>
                  <SelectTrigger className="h-8 w-24 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {supported.map(n => (
                      <SelectItem key={n} value={String(n)}>{n} teams</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {isCommissioner && !locked && previewChampWeek != null && (
              <p className="text-[11px] text-muted-foreground">
                That puts the championship in Week {previewChampWeek}, with {Number(startWeek) - 1} regular-season weeks.
              </p>
            )}

            {deadlineAfterPlayoffs && (
              <p className="text-[11px] text-amber-400">
                Heads up: the trade deadline is Week {deadlineWeek}, at or after the playoffs
                begin. That is allowed, but it usually means one of the two is set wrong.
              </p>
            )}

            {locked && (
              <p className="text-[11px] text-muted-foreground">
                {cfg.lock_reason === 'prize_schedule_set'
                  ? 'Locked: the prize schedule is set, and it is calculated from the regular-season length.'
                  : 'Locked: the draft has completed. Playoff structure is frozen at that point so the schedule and prize split cannot change under a league already playing.'}
              </p>
            )}

            {/* Stated because the ticket asked whether it was supported, and it is not.
                Saying so is better than a control that implies a capability the engine
                does not have. */}
            <p className="text-[11px] text-muted-foreground">
              Each playoff round is one week. Multi-week playoff matchups are not supported.
            </p>

            {isCommissioner && !locked && (
              <Button size="sm" onClick={save} disabled={!canEdit} className="h-8 text-xs">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function TradeDeadlineCard({
  leagueId, isCommissioner, onSaved,
}: {
  leagueId: string | undefined;
  isCommissioner: boolean;
  onSaved?: () => void;
}) {
  const { toast } = useToast();
  const [derived, setDerived] = useState<TradeDeadline | null>(null);
  const [week, setWeek] = useState<string>(String(DEFAULT_DEADLINE_WEEK));
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!leagueId) return;
    const { data } = await (supabase as any).rpc('nfl_trade_deadline', { p_league_id: leagueId });
    const d = parseTradeDeadline(data);
    setDerived(d);
    setEnabled(d.enabled);
    setWeek(String(d.week ?? DEFAULT_DEADLINE_WEEK));
  }, [leagueId]);

  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!leagueId) return;
    setSaving(true);
    try {
      const { error } = await (supabase as any).rpc('nfl_set_trade_deadline', {
        p_league_id: leagueId,
        p_deadline: { enabled, week: Number(week) },
      });
      if (error) throw error;
      await load();
      onSaved?.();
      toast({ title: 'Saved', description: 'Trade deadline updated.' });
    } catch (e: any) {
      // The RPC raises named errors; surface them rather than a generic failure. A
      // commissioner who picked week 30 should be told the range, not that "something
      // went wrong".
      const msg = e?.message === 'week_out_of_range'
        ? 'Pick a week between 8 and 15.'
        : e?.message === 'not_commissioner'
        ? 'Only the commissioner can change this.'
        : e?.message ?? 'Could not save the trade deadline.';
      toast({ title: 'Not saved', description: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const disabled = !isCommissioner || saving;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Trade Deadline</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-foreground">Enforce a trade deadline</p>
            {/* The DERIVED line, so the card shows the same thing the server refuses on. */}
            <p className="text-[11px] text-muted-foreground truncate">
              {derived ? tradeDeadlineLabel(derived) : 'Loading…'}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} disabled={disabled} />
        </div>

        {enabled && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">Closes after</label>
            <Select value={week} onValueChange={setWeek} disabled={disabled}>
              <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DEADLINE_WEEK_OPTIONS.map(w => (
                  <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Stated, because it is the whole rule and the half people forget. */}
        <p className="text-[11px] text-muted-foreground">
          After the deadline no proposal can be made or accepted, and any proposal still open
          expires unprocessed.
        </p>
        {/* Transparency, disclosed BEFORE the change rather than discovered after it. */}
        <p className="text-[11px] text-muted-foreground">
          Changing this mid-season posts a note to the league activity feed.
        </p>

        <Button size="sm" onClick={save} disabled={disabled} className="h-8 text-xs">
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </CardContent>
    </Card>
  );
}

export function WaiversCard({
  waivers, leagueStatus, leagueId, isCommissioner, onSaved,
}: {
  waivers: unknown;
  leagueStatus: string | null | undefined;
  leagueId: string | undefined;
  isCommissioner: boolean;
  onSaved: (next: { enabled: boolean; drop_lock_days: number; faab_budget: number }) => void;
}) {
  const { toast } = useToast();
  const stored = useMemo(() => parseWaiverSettings(waivers), [waivers]);
  const [enabled, setEnabled] = useState(stored.enabled);
  const [dropDays, setDropDays] = useState(String(stored.drop_lock_days));
  const [budget, setBudget] = useState(String(stored.faab_budget));
  const [postDraftDays, setPostDraftDays] = useState(String(stored.post_draft_waiver_days));
  const [saving, setSaving] = useState(false);

  // Re-seed when the stored value changes (a refetch, or another commissioner saving).
  useEffect(() => {
    setEnabled(stored.enabled);
    setDropDays(String(stored.drop_lock_days));
    setBudget(String(stored.faab_budget));
    setPostDraftDays(String(stored.post_draft_waiver_days));
  }, [stored]);

  const faabLocked = isFaabLocked(leagueStatus);
  const disabled = !isCommissioner || saving;

  const save = async () => {
    if (!leagueId) return;
    const d = Number(dropDays.trim());
    if (!Number.isInteger(d) || d < 0) {
      toast({ title: 'Not a length', description: 'Drop lock must be a whole number of days, or 0 for none.', variant: 'destructive' });
      return;
    }
    const b = Number(budget.trim());
    if (!Number.isInteger(b) || b < 0) {
      toast({ title: 'Not a budget', description: 'FAAB budget must be a whole number.', variant: 'destructive' });
      return;
    }

    // The budget is omitted entirely once the season is under way, rather than sent and
    // refused: sending an unchanged value would pass (the RPC allows a no-op), but sending
    // a CHANGED one raises FAAB_LOCKED, and the input is disabled precisely so that cannot
    // happen. Omitting keeps the payload honest about what this save is asking for.
    const pd = Number(postDraftDays.trim());
    if (!Number.isInteger(pd) || pd < 0) {
      toast({ title: 'Not a length', description: 'Post-draft waivers must be a whole number of days, or 0 for none.', variant: 'destructive' });
      return;
    }
    const payload: Record<string, unknown> = { enabled, drop_lock_days: d, post_draft_waiver_days: pd };
    if (!faabLocked) payload.faab_budget = b;

    setSaving(true);
    const { data, error } = await (supabase as any).rpc('nfl_set_waiver_settings', {
      p_league_id: leagueId,
      p_waivers: payload,
    });
    setSaving(false);
    if (error) {
      toast({ title: 'Could not save', description: error.message, variant: 'destructive' });
      return;
    }
    onSaved(parseWaiverSettings(data));
    toast({ title: 'Saved', description: 'Waiver settings updated.' });
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Waivers</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        <label className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-xs font-medium text-foreground">Use waivers</span>
            <span className="block text-[11px] text-muted-foreground">
              Off means pure first-come free agency — no bidding, no drop lock.
            </span>
          </span>
          <Switch
            data-testid="waivers-enabled"
            checked={enabled}
            disabled={disabled}
            onCheckedChange={setEnabled}
            className="shrink-0 mt-0.5"
          />
        </label>

        <label className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-xs font-medium text-foreground">Drop lock</span>
            <span className="block text-[11px] text-muted-foreground">
              Days a dropped player sits on waivers before anyone can add them. 0 sends them
              straight to free agency.
            </span>
          </span>
          <Input
            data-testid="waivers-drop-lock-days"
            type="number" min={0} inputMode="numeric"
            disabled={disabled || !enabled}
            value={dropDays}
            onChange={e => setDropDays(e.target.value)}
            className="h-8 w-20 shrink-0 text-xs"
          />
        </label>

        <label className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-xs font-medium text-foreground">Post-draft waivers</span>
            <span className="block text-[11px] text-muted-foreground">
              Days all players sit on waivers after your draft, so nobody can vulture the
              pool the moment it ends. 0 = straight to free agency.
            </span>
          </span>
          <Input
            data-testid="waivers-post-draft-days"
            type="number" min={0} inputMode="numeric"
            disabled={disabled || !enabled}
            value={postDraftDays}
            onChange={e => setPostDraftDays(e.target.value)}
            className="h-8 w-20 shrink-0 text-xs"
          />
        </label>

        <label className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block text-xs font-medium text-foreground">FAAB budget</span>
            <span className="block text-[11px] text-muted-foreground">
              {faabLocked
                ? 'Locked — the budget can only be changed before week 1, because teams have already bid against the current one.'
                : 'Each team\u2019s season budget for waiver bids. Changing it now resets every team to the new amount.'}
            </span>
          </span>
          <Input
            data-testid="waivers-faab-budget"
            type="number" min={0} inputMode="numeric"
            disabled={disabled || faabLocked}
            value={budget}
            onChange={e => setBudget(e.target.value)}
            className="h-8 w-24 shrink-0 text-xs"
          />
        </label>

        {/* SAB-1026: the tie-break is DISCLOSED, not only implemented — a rule that
            decides money and is invisible to the manager it decides against is the
            SAB-722 copy-truth class. This sentence and the resolver's ordering are the
            same fact: worst team wins a tie, by live standings at resolution time. */}
        {/* Platform-wide, not per-league in v1 — stated so a commissioner does not go
            looking for a control that does not exist. */}
        <p className="text-[11px] text-muted-foreground border-t border-border pt-3">
          {/* SAB-1142c: see NFLPlayersPage — one line cannot describe two classes that
              differ by five days. */}
          {/* SAB-1142c: see NFLPlayersPage — nightly is UTC-anchored and shifts in winter,
              weekly is ET-anchored and does not. */}
          Recently dropped players clear overnight at 2:00 AM ET (1:00 AM once clocks change);
          players whose game has already been played clear on your league&apos;s weekly waiver
          day at 2:00 AM ET. Highest bid wins; ties go to
          the team lowest in the standings. Players are locked at their
          game’s kickoff and clear to waivers Wednesday morning. These are league-wide and
          not configurable.
        </p>

        {isCommissioner && (
          <Button data-testid="save-waivers" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving\u2026' : 'Save waiver settings'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

// Exported for test only: the pure scoping is covered in nflPositionLimits.test.ts, but
// that cannot prove THIS component calls it. Shipping a correct helper the caller ignores
// is exactly how SAB-1016's overlay became unreachable on desktop.
export function PositionLimitsCard({
  rosterSlots, positionLimits, leagueId, isCommissioner, onSaved,
}: {
  rosterSlots: unknown;
  positionLimits: unknown;
  leagueId: string | undefined;
  isCommissioner: boolean;
  onSaved: (next: Record<string, number>) => void;
}) {
  const { toast } = useToast();
  const [rows, setRows] = useState<{ position: string; slot_base: string; priority: number }[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Seed the inputs from what is stored, through the SAME parser the draft room and the
  // parity test use -- so a hand-edited or legacy blob is shown exactly as it will be read.
  useEffect(() => {
    const parsed = leaguePositionLimits(positionLimits);
    setDraft(Object.fromEntries(Object.entries(parsed).map(([k, v]) => [k, String(v)])));
  }, [positionLimits]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('nfl_slot_eligibility' as any)
        .select('position, slot_base, priority')
        .eq('is_alias', false)
        .order('priority', { ascending: true });
      if (cancelled) return;
      if (error || !data) { setFailed(true); return; }
      setRows(data as any);
    })();
    return () => { cancelled = true; };
  }, []);

  // SAB-989 follow-up: the grid offered a cap for all 20 non-alias positions in the
  // eligibility table, including every IDP position in a league with no IDP slot. Capping a
  // position the league cannot roster at all is not a setting, it is noise -- and it is the
  // same defect SAB-995 fixed on the players list, so it uses the SAME derivation rather
  // than a second opinion about what a league can hold.
  const { startable } = useLeagueRosterability(rosterSlots);

  const positions = useMemo(() => {
    if (!rows) return [] as string[];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const r of [...rows].sort((a, b) => a.priority - b.priority)) {
      if (!seen.has(r.position)) { seen.add(r.position); out.push(r.position); }
    }

    // FB folds into RB, and unrosterable positions are dropped — both in
    // visiblePositionLimits, which carries the reasoning and the null contract.
    //
    // NOTE, deliberately not "fixed" here: guard_nfl_position_limit counts
    // `p2.position = v_position`, an EXACT match, so an RB cap does not constrain an FB.
    // Hiding the input leaves FB uncapped rather than folded — harmless today (zero FB
    // active_2026, zero leagues with position_limits set) and writing an unrequested FB key
    // would invent a rule the commissioner never set. Flagged on the ticket.
    return visiblePositionLimits(out, startable);
  }, [rows, startable]);

  /**
   * Per position, the smallest cap that still lets a roster be completed.
   *
   * A slot_base only ONE position is eligible for is a slot only that position can fill, so
   * the league needs at least as many of that position as it has such slots. A base several
   * positions share (FLEX, BN, IR) constrains nobody in particular and is skipped.
   */
  const minimums = useMemo(() => {
    const min = new Map<string, number>();
    if (!rows) return min;
    const byBase = new Map<string, string[]>();
    for (const r of rows) byBase.set(r.slot_base, [...(byBase.get(r.slot_base) ?? []), r.position]);
    for (const [base, posns] of byBase) {
      const unique = [...new Set(posns)];
      if (unique.length !== 1) continue;
      const count = slotsForBase(base, rosterSlots).length;
      if (count > 0) min.set(unique[0], Math.max(min.get(unique[0]) ?? 0, count));
    }
    return min;
  }, [rows, rosterSlots]);

  const save = async () => {
    if (!leagueId) return;
    const limits: Record<string, number> = {};
    for (const [pos, raw] of Object.entries(draft)) {
      const t = (raw ?? '').trim();
      if (t === '') continue;                      // blank = no limit
      const n = Number(t);
      if (!Number.isInteger(n) || n < 0) {
        toast({ title: 'Not a limit', description: `${pos}: enter a whole number, or leave it blank for no limit.`, variant: 'destructive' });
        return;
      }
      const floor = minimums.get(pos) ?? 0;
      if (n < floor) {
        toast({
          title: 'That limit cannot be filled',
          description: `This league has ${floor} ${pos} starting slot${floor === 1 ? '' : 's'}, so ${pos} cannot be capped below ${floor}.`,
          variant: 'destructive',
        });
        return;
      }
      limits[pos] = n;
    }

    setSaving(true);
    // Targeted server-side key write. NOT a full-object settings PATCH: settings also holds
    // draft_current_pick and draft_pick_deadline, which move on every pick, and caps are set
    // precisely while a draft is running.
    const { error } = await (supabase as any).rpc('nfl_set_position_limits', {
      p_league_id: leagueId,
      p_limits: limits,
    });
    setSaving(false);
    if (error) {
      toast({ title: 'Could not save', description: error.message, variant: 'destructive' });
      return;
    }
    onSaved(limits);
    const n = Object.keys(limits).length;
    toast({ title: 'Saved', description: n === 0 ? 'Position limits cleared.' : `${n} position limit${n === 1 ? '' : 's'} saved.` });
  };

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-foreground">Position Limits</CardTitle>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        <p className="text-xs text-muted-foreground">
          The most of each position one team may hold, counting bench and injured reserve.
          Leave blank for no limit.
        </p>
        {failed && <p className="text-xs text-muted-foreground">Position list is unavailable right now.</p>}
        {!failed && !rows && <p className="text-xs text-muted-foreground">Loading…</p>}
        {rows && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {positions.map(pos => {
              const floor = minimums.get(pos) ?? 0;
              return (
                <label key={pos} className="flex items-center gap-2 text-xs">
                  <span className="w-12 shrink-0 font-display font-bold text-foreground">{pos}</span>
                  <Input
                    data-testid={`position-limit-${pos}`}
                    type="number"
                    min={floor}
                    inputMode="numeric"
                    placeholder="—"
                    disabled={!isCommissioner || saving}
                    value={draft[pos] ?? ''}
                    onChange={e => setDraft(d => ({ ...d, [pos]: e.target.value }))}
                    className="h-8 text-xs"
                  />
                </label>
              );
            })}
          </div>
        )}
        {isCommissioner && rows && (
          <Button data-testid="save-position-limits" size="sm" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save limits'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function NFLSettingsPage({ initialTab = 'menu' }: { initialTab?: SettingsTab }) {
  const { user } = useAuth();
  const { currentLeague, currentTeam, teams, currentWeek, isLoading: contextLoading } = useNFLLeague();

  // SAB-886: this league's own slots, split as the three cards render them.
  const { starters: STARTER_SLOTS, bench: BENCH_SLOTS, ir: IR_SLOTS } = useMemo(
    () => partitionSlots(currentLeague?.settings?.roster_slots),
    [currentLeague?.settings],
  );
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [tab, setTab] = useState<SettingsTab>(initialTab);
  // SAB-512 Phase 3: follow the deep-link section (e.g. the mobile More sheet's
  // Wallet -> funds) when the route changes, without overriding in-page nav.
  useEffect(() => { setTab(initialTab); }, [initialTab]);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [resettingLogo, setResettingLogo] = useState<string | null>(null);
  const [leagueData, setLeagueData] = useState<any>(null);
  const [copied, setCopied] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leavingLeague, setLeavingLeague] = useState(false);

  // SAB-566: these were declared INSIDE the `notifications` and `commissioner`
  // tab branches below — which sit after several `if (tab === …) return` early
  // returns. That made them CONDITIONAL hooks: switching tabs changed the hook
  // count and threw React #310 ("rendered fewer hooks than expected"), crashing
  // the settings page to the error boundary (repro: Wallet/Notifications tab).
  // Hooks must be unconditional, so they live here; the tab bodies keep their
  // helper closures + JSX and just reference this state.
  const [notifPrefs, setNotifPrefs] = useState<Record<string, boolean>>({});
  const [notifLoading, setNotifLoading] = useState(true);
  const [notifSaving, setNotifSaving] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [broadcastMsg, setBroadcastMsg] = useState('');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [removingMember, setRemovingMember] = useState<string | null>(null);

  // Lazy-fetch notification prefs only when that tab is open (hook itself is
  // unconditional; the body no-ops for every other tab).
  useEffect(() => {
    if (tab !== 'notifications' || !user) return;
    supabase
      .from('nfl_notification_preferences')
      .select('preferences')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.preferences) setNotifPrefs(data.preferences as Record<string, boolean>);
        setNotifLoading(false);
      });
  }, [user, tab]);

  const isCommissioner = currentLeague?.commissioner_id === user?.id;
  const canLeave = !isCommissioner && (currentLeague?.status === 'pre_draft' || leagueData?.status === 'pre_draft');

  // SAB-1108: this used to DELETE the team and membership rows straight from the client and
  // then insert an activity row. Every part of it was broken and none of it was detectable:
  // both deletes matched 0 rows under RLS (no DELETE policy) and neither result was checked,
  // the activity insert named columns that do not exist (event_type/message vs
  // activity_type/title/description), and no refund happened at all. The user saw a "Left
  // League" toast, was navigated away, and was still in the league and still charged.
  //
  // All of it now happens server-side in one transaction, and the entry fee comes back.
  const handleLeaveLeague = async () => {
    if (!currentLeague || !user || !currentTeam) return;
    setLeavingLeague(true);
    try {
      const { data, error } = await supabase.functions.invoke('nfl-leave-league', {
        body: { league_id: currentLeague.id },
      });
      if (error) throw error;
      if (!data?.ok) throw new Error(data?.error || 'Could not leave league');

      const refunded = Number(data.refund_amount ?? 0);
      toast({
        title: 'Left League',
        description: refunded > 0
          ? `You have left ${currentLeague.name}. Your $${refunded.toFixed(2)} entry fee has been refunded to your wallet.`
          : `You have left ${currentLeague.name}.`,
      });
      navigate('/nfl');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({ title: 'Could not leave league', description: message, variant: 'destructive' });
    } finally {
      setLeavingLeague(false);
      setShowLeaveConfirm(false);
    }
  };

  // Fetch league data + members
  useEffect(() => {
    if (!currentLeague) return;
    let cancelled = false;

    async function load() {
      // Full league record
      const { data: league } = await supabase
        .from('nfl_leagues')
        .select('*')
        .eq('id', currentLeague!.id)
        .single();

      if (cancelled) return;
      setLeagueData(league);

      // Members
      const { data: memberData } = await supabase
        .from('nfl_league_members')
        .select('user_id, role')
        .eq('league_id', currentLeague!.id);

      if (cancelled || !memberData) return;

      // Get profiles
      const userIds = memberData.map(m => m.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', userIds);

      const profileMap: Record<string, string> = {};
      if (profiles) for (const p of profiles) profileMap[p.id] = p.username || 'Unknown';

      // Get team names -- and the team itself, for the SAB-1004 logo reset below.
      const teamByOwner: Record<string, { id: string; name: string; logo_url: string | null }> = {};
      for (const t of teams) teamByOwner[t.owner_id] = { id: t.id, name: t.name, logo_url: t.logo_url };

      // `memberData` types as a SelectQueryError union (the generated types do not model
      // this table), so every `m.user_id` access is its own type error. Reading it ONCE per
      // row keeps SAB-1004's two extra lookups from adding two more, and collapses the
      // existing ones -- net fewer type errors than before this change.
      setMembers(memberData.map(m => {
        const uid = (m as { user_id: string; role: string }).user_id;
        const role = (m as { user_id: string; role: string }).role;
        const team = teamByOwner[uid];
        return {
          user_id: uid,
          role,
          username: profileMap[uid] || null,
          team_name: team?.name || null,
          team_id: team?.id || null,
          team_logo_url: team?.logo_url || null,
        };
      }));
    }

    load();
    return () => { cancelled = true; };
  }, [currentLeague, teams]);

  const copyInviteCode = () => {
    const code = currentLeague?.invite_code;
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentLeague || !currentTeam) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Settings className="h-10 w-10 text-muted-foreground mb-4" />
        <p className="text-sm text-muted-foreground">Join a league to view settings</p>
      </div>
    );
  }

  /* ================================================================ */
  /*  Settings Menu                                                    */
  /* ================================================================ */
  if (tab === 'menu') {
    return (
      <div className="space-y-4">
        <h1 className="text-lg font-display font-bold text-foreground">Settings</h1>
        <Card className="bg-card border-border">
          <CardContent className="p-2 space-y-0.5">
            {MENU_ITEMS.filter(item => !item.commishOnly || isCommissioner).map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted hover:text-foreground transition-all"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span className="flex-1 text-left">{item.label}</span>
                  {item.commishOnly && (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[8px] px-1.5 py-0">
                      COMMISH
                    </Badge>
                  )}
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Leave League — non-commissioner, pre-draft only */}
        {canLeave && (
          <Card className="bg-card border-red-800/30">
            <CardContent className="p-4">
              {showLeaveConfirm ? (
                <div className="space-y-3">
                  <p className="text-sm text-red-400 font-medium">Are you sure you want to leave this league?</p>
                  <p className="text-xs text-muted-foreground">
                    Your team and roster will be removed from this league.
                    {Number(leagueData?.entry_fee ?? currentLeague?.entry_fee ?? 0) > 0
                      ? ' Your entry fee will be refunded to your wallet.'
                      : ''}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 border-border"
                      onClick={() => setShowLeaveConfirm(false)}
                      disabled={leavingLeague}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-red-600 hover:bg-red-500 text-white"
                      onClick={handleLeaveLeague}
                      disabled={leavingLeague}
                    >
                      {leavingLeague ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <LogOut className="h-4 w-4 mr-1" />}
                      Leave
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowLeaveConfirm(true)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:bg-red-500/5 transition-all"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="flex-1 text-left">Leave League</span>
                </button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  /* ================================================================ */
  /*  Back button helper                                               */
  /* ================================================================ */
  const BackButton = () => (
    <button
      onClick={() => setTab('menu')}
      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mb-3"
    >
      <ChevronLeft className="h-3.5 w-3.5" />
      Settings
    </button>
  );

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h2 className="text-base font-display font-bold text-foreground mb-3">{children}</h2>
  );

  const ReadOnlyBadge = () => !isCommissioner ? (
    <Badge className="bg-muted/10 text-muted-foreground border-border/20 text-[8px] px-1.5 py-0 ml-2">
      VIEW ONLY
    </Badge>
  ) : null;

  const SettingRow = ({ label, value, sublabel }: { label: string; value: React.ReactNode; sublabel?: string }) => (
    <div className="flex items-center justify-between py-2.5 border-b border-border/50 last:border-b-0">
      <div>
        <p className="text-sm text-foreground">{label}</p>
        {sublabel && <p className="text-[10px] text-muted-foreground">{sublabel}</p>}
      </div>
      <div className="text-sm text-muted-foreground text-right">{value}</div>
    </div>
  );

  /* ================================================================ */
  /*  League Settings                                                  */
  /* ================================================================ */
  if (tab === 'league') {
    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>League Settings <ReadOnlyBadge /></SectionTitle>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <SettingRow label="League Name" value={currentLeague.name} />
            <SettingRow label="Season" value={leagueData?.season_year ?? '—'} />
            <SettingRow label="Max Teams" value={currentLeague.team_count} />
            <SettingRow label="Status" value={
              <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                {leagueData?.status || currentLeague.status || 'active'}
              </Badge>
            } />
            <SettingRow label="Current Week" value={`Week ${currentWeek}`} />
            <SettingRow label="Invite Code" value={
              <div className="flex items-center gap-2">
                <code className="text-xs text-primary bg-primary/5 px-2 py-0.5 rounded">
                  {currentLeague.invite_code || '-'}
                </code>
                {currentLeague.invite_code && (
                  <button onClick={copyInviteCode} className="text-muted-foreground hover:text-foreground transition-colors">
                    {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                )}
              </div>
            } />
          </CardContent>
        </Card>

        {/* SAB-1075: playoff structure lives on the League tab because that is where a
            member looks to answer "how is this league set up". Read-only for members and
            once the league locks; editable for a commissioner before that. */}
        <PlayoffConfigCard
          leagueId={currentLeague.id}
          isCommissioner={isCommissioner}
          onSaved={() => {
            // The standings page reads playoff_teams for its playoff cut line and the
            // schedule reads playoff_start_week, so a stale context would draw the line in
            // the wrong place right after a change.
            queryClient.invalidateQueries({ queryKey: ['nfl-leagues'] });
          }}
        />

        {/* SAB-1074: no longer commissioner-gated. Members could not see the tiebreaker
            that orders their own standings. */}
        <CommissionerTiebreaker
          leagueId={currentLeague.id}
          leagueData={leagueData}
          setLeagueData={setLeagueData}
          isCommissioner={isCommissioner}
        />
      </div>
    );
  }

  /* ================================================================ */
  /*  Members                                                          */
  /* ================================================================ */
  if (tab === 'members') {
    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Members ({members.length}/{currentLeague.team_count})</SectionTitle>
        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {members.map((m, i) => (
              <div
                key={m.user_id}
                className={cn(
                  'flex items-center justify-between px-4 py-3 border-b border-border/50 last:border-b-0',
                  m.user_id === user?.id && 'bg-primary/[0.03]'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    'h-8 w-8 rounded-full border flex items-center justify-center shrink-0',
                    m.user_id === user?.id ? 'bg-primary/10 border-primary/20' : 'bg-muted/50 border-border'
                  )}>
                    <span className={cn(
                      'text-xs font-bold',
                      m.user_id === user?.id ? 'text-primary' : 'text-foreground'
                    )}>
                      {(m.username || '?').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-foreground">{m.username || 'Unknown'}</p>
                    {m.team_name && <p className="text-[10px] text-muted-foreground">{m.team_name}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* SAB-1004 moderation lever. Shown only when there is a logo to clear, so
                      the control is never a no-op; RLS could not do this from the client, so
                      it goes through the commissioner-gated nfl_reset_team_logo RPC. */}
                  {isCommissioner && m.team_id && m.team_logo_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid="reset-team-logo"
                      disabled={resettingLogo === m.team_id}
                      className="h-6 px-2 text-[10px] text-muted-foreground hover:text-destructive"
                      onClick={async () => {
                        setResettingLogo(m.team_id);
                        const { error } = await (supabase as any).rpc('nfl_reset_team_logo', { p_team_id: m.team_id });
                        setResettingLogo(null);
                        if (error) {
                          toast({ title: 'Could not reset logo', description: error.message, variant: 'destructive' });
                          return;
                        }
                        setMembers(prev => prev.map(x => x.user_id === m.user_id ? { ...x, team_logo_url: null } : x));
                        queryClient.invalidateQueries({ queryKey: ['nfl-teams', currentLeague?.id] });
                        toast({ title: 'Logo reset', description: `${m.team_name || 'That team'} is back to its monogram.` });
                      }}
                    >
                      Reset logo
                    </Button>
                  )}
                  <Badge className={cn(
                    'text-[9px] px-1.5 py-0 border',
                    m.role === 'commissioner'
                      ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                      : 'bg-muted/10 text-muted-foreground border-border/20'
                  )}>
                    {m.role === 'commissioner' ? 'Commissioner' : 'Member'}
                  </Badge>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ================================================================ */
  /*  Roster Settings                                                  */
  /* ================================================================ */
  if (tab === 'roster') {
    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Roster Settings <ReadOnlyBadge /></SectionTitle>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Starting Lineup ({STARTER_SLOTS.length})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1.5">
              {STARTER_SLOTS.map(slot => (
                <Badge key={slot} variant="outline" className="text-[10px] border-border text-muted-foreground">
                  {slot}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Bench ({BENCH_SLOTS.length})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1.5">
              {BENCH_SLOTS.map(slot => (
                <Badge key={slot} variant="outline" className="text-[10px] border-border text-muted-foreground">BN</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Injured Reserve ({IR_SLOTS.length})</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-wrap gap-1.5">
              {IR_SLOTS.map(slot => (
                <Badge key={slot} variant="outline" className="text-[10px] border-red-800/30 text-red-400">IR</Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        <PositionEligibilityCard rosterSlots={currentLeague?.settings?.roster_slots} />

        <PositionLimitsCard
          rosterSlots={currentLeague?.settings?.roster_slots}
          positionLimits={(currentLeague?.settings as any)?.position_limits}
          leagueId={currentLeague?.id}
          isCommissioner={isCommissioner}
          onSaved={next => {
            // Keep the page honest until the context refetches: the draft room reads caps
            // from the same settings object, and a stale card would show a cap the room is
            // already enforcing (or not).
            queryClient.invalidateQueries({ queryKey: ['nfl-leagues'] });
            setLeagueData((d: any) => (d ? { ...d, settings: { ...(d.settings || {}), position_limits: next } } : d));
          }}
        />

        <TradeDeadlineCard
          leagueId={currentLeague?.id}
          isCommissioner={isCommissioner}
        />

        <WaiversCard
          waivers={(currentLeague?.settings as any)?.waivers}
          leagueStatus={currentLeague?.status}
          leagueId={currentLeague?.id}
          isCommissioner={isCommissioner}
          onSaved={next => {
            // Same reason as Position Limits: nfl-add-drop and the players page both read
            // these rules, so a stale card would show a lock the server is no longer applying.
            queryClient.invalidateQueries({ queryKey: ['nfl-leagues'] });
            setLeagueData((d: any) => (d ? { ...d, settings: { ...(d.settings || {}), waivers: next } } : d));
          }}
        />
      </div>
    );
  }

  /* ================================================================ */
  /*  Scoring Settings                                                 */
  /* ================================================================ */
  if (tab === 'scoring') {
    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Scoring Rules</SectionTitle>
        <NFLScoringSettings
          leagueId={currentLeague.id}
          isCommissioner={isCommissioner}
          leagueStatus={leagueData?.status || currentLeague.status || 'pre_draft'}
        />
      </div>
    );
  }

  /* ================================================================ */
  /*  Sabotage Settings                                                */
  /* ================================================================ */
  if (tab === 'sabotages') {
    const budget = leagueData?.sabotage_budget ?? 100;

    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Sabotage Settings <ReadOnlyBadge /></SectionTitle>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <SettingRow label="Starting Budget" value={`${budget} SP per team`} />
            <SettingRow label="Store Pricing" value="Common $2 · Rare $5 · Legendary $10" sublabel="Buy specific cards from the store" />
            <SettingRow label="Legendary Limit" value="1 per league per week" />
          </CardContent>
        </Card>

        {isCommissioner && <CommissionerCardCustomization leagueId={currentLeague.id} leagueData={leagueData} setLeagueData={setLeagueData} />}
      </div>
    );
  }

  /* ================================================================ */
  /*  Draft Settings                                                   */
  /* ================================================================ */
  if (tab === 'draft') {
    const draftType = leagueData?.draft_type || 'snake';
    // SAB-512: canonical pick-timer fallback matches nfl-create-league (slow -> 8h,
    // snake/live -> 90s), not the stray 120 this used before.
    const pickTimer = leagueData?.draft_pick_timer || (draftType === 'slow' ? 28800 : 90);
    const draftStart = leagueData?.draft_start_time;
    const draftStatus = leagueData?.draft_status || 'pending';
    // SAB-803: display fallback for a league whose draft has not been initialized yet
    // (settings.draft_rounds is written by nfl-initialize-draft). Tracks the 19-round
    // server default. An already-drafted league shows its own stored value, so the six
    // existing 21-round leagues keep reading 21 here. NOT the roster cap, which stays 21.
    const rounds = leagueData?.settings?.draft_rounds || 19;

    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Draft Settings <ReadOnlyBadge /></SectionTitle>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <SettingRow label="Draft Type" value={
              <Badge className="bg-primary/10 text-primary border-primary/20 text-[10px]">
                {draftType === 'snake' ? 'Snake' : draftType === 'auction' ? 'Auction' : draftType === 'linear' ? 'Linear' : draftType}
              </Badge>
            } />
            <SettingRow label="Pick Timer" value={`${pickTimer}s`} />
            <SettingRow label="Rounds" value={rounds} />
            <SettingRow label="Status" value={
              <Badge className={cn(
                'text-[10px] border',
                draftStatus === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : draftStatus === 'in_progress' ? 'bg-green-500/10 text-green-400 border-green-500/20'
                  : 'bg-muted/10 text-muted-foreground border-border/20'
              )}>
                {draftStatus}
              </Badge>
            } />
            <SettingRow label="Scheduled" value={
              draftStart
                ? formatDraftTime(draftStart)   /* SAB-788: with zone abbrev */
                : <span className="text-muted-foreground">Not scheduled</span>
            } />
          </CardContent>
        </Card>

        {/* SAB-917: was a read-only list. Now an editor for commissioners, still read-only
            for everyone else. The old card also sorted by draft_position but RENDERED
            {i + 1} -- a counter, not the field -- so non-contiguous, duplicated or null
            positions displayed as a tidy 1..N and hid themselves. Same defect SAB-918
            fixed in the draft room rail. */}
        {isCommissioner ? (
          <CommissionerDraftOrder
            leagueId={currentLeague.id}
            teams={teams as unknown as { id: string; name: string; draft_position: number | null }[]}
            myTeamId={currentTeam.id}
            draftStatus={draftStatus}
          />
        ) : (
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Draft Order</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-1">
            {[...teams]
              .sort((a, b) => {
                const ap = (a as any).draft_position, bp = (b as any).draft_position;
                if (ap != null && bp != null && ap !== bp) return ap - bp;
                if (ap != null && bp == null) return -1;
                if (ap == null && bp != null) return 1;
                return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
              })
              .map(t => (
                <div key={t.id} className={cn(
                  'flex items-center gap-2 py-1.5 px-2 rounded text-sm',
                  t.id === currentTeam.id && 'bg-primary/5'
                )}>
                  {/* SAB-918: the team's OWN position, never a counter. '—' when unassigned,
                      because a tidy number for a team with no slot is a false statement. */}
                  <span className="w-5 text-xs text-muted-foreground text-center">
                    {(t as any).draft_position ?? '—'}
                  </span>
                  <span className={cn('text-foreground', t.id === currentTeam.id && 'text-primary')}>
                    {t.name}
                  </span>
                </div>
              ))
            }
          </CardContent>
        </Card>
        )}
      </div>
    );
  }

  /* ================================================================ */
  /*  Funds                                                            */
  /* ================================================================ */
  if (tab === 'funds') {
    // SAB-566 Fix 1: no interstitial. The settings "Wallet" entry goes straight
    // to the account-level /wallet — redirect on mount, zero rendered content.
    return <Navigate to="/wallet" replace />;
  }

  /* ================================================================ */
  /*  Bonuses & Survivor (SAB-288 — wired to league_bonuses +          */
  /*  league_survivor_config; awards via award-manual-bonus edge fn)   */
  /* ================================================================ */
  if (tab === 'bonuses') {
    return (
      <div className="space-y-4">
        <BackButton />
        <NFLBonusesSection
          leagueId={currentLeague.id}
          prizePool={Number(leagueData?.prize_pool ?? 0)}
          currentWeek={currentLeague.current_week ?? 1}
          playoffStartWeek={
            leagueData?.playoff_start_week ?? currentLeague.playoff_start_week ?? 15
          }
          teams={teams.map(t => ({ id: t.id, name: t.name, owner_id: t.owner_id }))}
          isCommissioner={isCommissioner}
        />
      </div>
    );
  }

  /* ================================================================ */
  /*  Notification Preferences                                         */
  /* ================================================================ */
  if (tab === 'notifications') {
    const NOTIF_CATEGORIES = [
      { key: 'draft', label: 'Draft Notifications', desc: 'Draft started, your turn to pick, draft complete' },
      { key: 'sabotage', label: 'Sabotage Alerts', desc: 'When someone plays a card on your matchup' },
      { key: 'matchup_results', label: 'Matchup Results', desc: 'Weekly results, close game alerts, championship' },
      { key: 'trades', label: 'Trade & Waiver Alerts', desc: 'Trade proposals, accepted/declined, waiver results' },
      { key: 'lineup_reminders', label: 'Lineup Reminders', desc: LINEUP_REMINDER_TOGGLE_DESC },
      { key: 'league', label: 'League Updates', desc: 'New members joining your league' },
    ]

    // notif state + fetch effect hoisted to the component top (SAB-566).
    const togglePref = async (key: string) => {
      if (!user) return
      const newVal = !(notifPrefs[key] !== false)
      const updated = { ...notifPrefs, [key]: newVal }
      setNotifPrefs(updated)
      setNotifSaving(true)

      await supabase
        .from('nfl_notification_preferences')
        .upsert({
          user_id: user.id,
          preferences: updated,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id' })

      setNotifSaving(false)
    }

    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Notifications</SectionTitle>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {notifLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              NOTIF_CATEGORIES.map((cat, i) => {
                const enabled = notifPrefs[cat.key] !== false
                return (
                  <div
                    key={cat.key}
                    className={cn(
                      'flex items-center justify-between px-4 py-3',
                      i < NOTIF_CATEGORIES.length - 1 && 'border-b border-border/50'
                    )}
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="text-sm text-foreground">{cat.label}</p>
                      <p className="text-[10px] text-muted-foreground">{cat.desc}</p>
                    </div>
                    <button
                      onClick={() => togglePref(cat.key)}
                      disabled={notifSaving}
                      className={cn(
                        'w-10 h-5 rounded-full transition-colors relative shrink-0',
                        enabled ? 'bg-primary' : 'bg-muted'
                      )}
                    >
                      <div className={cn(
                        'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                        enabled ? 'translate-x-5' : 'translate-x-0.5'
                      )} />
                    </button>
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center">
          Push notifications require the mobile app with notifications enabled in your device settings.
        </p>

        <SectionTitle>Email Notifications</SectionTitle>

        <Card className="bg-card border-border">
          <CardContent className="p-0">
            {notifLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : (
              [
                { key: 'email_draft', label: 'Draft Emails', desc: 'Draft started, draft complete with roster' },
                { key: 'email_sabotage', label: 'Sabotage Emails', desc: 'When someone plays a card on your matchup' },
                { key: 'email_matchup_results', label: 'Matchup Emails', desc: 'Weekly preview, results, playoff updates' },
                { key: 'email_trades', label: 'Trade & Waiver Emails', desc: 'Trade proposals, waiver claim results' },
                { key: 'email_league', label: 'League Emails', desc: 'Invite emails, league announcements' },
              ].map((cat, i, arr) => {
                const enabled = notifPrefs[cat.key] !== false
                return (
                  <div
                    key={cat.key}
                    className={cn(
                      'flex items-center justify-between px-4 py-3',
                      i < arr.length - 1 && 'border-b border-border/50'
                    )}
                  >
                    <div className="min-w-0 flex-1 mr-3">
                      <p className="text-sm text-foreground">{cat.label}</p>
                      <p className="text-[10px] text-muted-foreground">{cat.desc}</p>
                    </div>
                    <button
                      onClick={() => togglePref(cat.key)}
                      disabled={notifSaving}
                      className={cn(
                        'w-10 h-5 rounded-full transition-colors relative shrink-0',
                        enabled ? 'bg-primary' : 'bg-muted'
                      )}
                    >
                      <div className={cn(
                        'absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform',
                        enabled ? 'translate-x-5' : 'translate-x-0.5'
                      )} />
                    </button>
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>

        <p className="text-[10px] text-muted-foreground text-center">
          Email notifications are sent to the email address on your account.
        </p>
      </div>
    )
  }

  /* ================================================================ */
  /*  Commissioner Tools                                               */
  /* ================================================================ */
  if (tab === 'commissioner') {
    if (!isCommissioner) {
      return (
        <div className="space-y-4">
          <BackButton />
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-10 text-center">
              <ShieldAlert className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Commissioner access only</p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // commissioner state hoisted to the component top (SAB-566).
    const callUpdateLeague = async (updates: Record<string, unknown>, successMsg: string) => {
      const actionKey = Object.keys(updates).join('-');
      setActionLoading(actionKey);
      try {
        const { data: session } = await supabase.auth.getSession();
        const token = session?.session?.access_token;
        if (!token) return;

        const res = await fetch(
          `${(supabase as any).supabaseUrl}/functions/v1/nfl-update-league`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              league_id: currentLeague.id,
              updates,
            }),
          }
        );
        const data = await res.json();
        if (data.error) {
          toast({ title: 'Error', description: data.error, variant: 'destructive' });
        } else {
          toast({ title: 'Done', description: successMsg });
        }
      } catch (err) {
        toast({ title: 'Error', description: String(err), variant: 'destructive' });
      } finally {
        setActionLoading(null);
      }
    };

    /**
     * SAB-922: randomize the draft order.
     *
     * ── WHAT THIS REPLACED, AND WHY IT WAS NOT A SMALL BUG ──────────────────
     *
     * The previous implementation looped N sequential client-side UPDATEs against
     * nfl_teams. That table's UPDATE policy is OWNERSHIP-ONLY --
     * `USING (owner_id = auth.uid())` -- so RLS silently dropped every row except the
     * commissioner's own, and a policy-filtered UPDATE returns success with ZERO ROWS
     * rather than an error. There was no row-count check, so nothing noticed.
     *
     * Measured on staging against a clean 1..8 league:
     *
     *   LANDED             1
     *   SILENTLY_DROPPED   7
     *   after              2,3,4,5,6,7,8,8      <- duplicate 8, missing 1
     *
     * with a "Draft Order Randomized" toast. And nfl-initialize-draft:122 sorts on
     * (draft_position || 999), so the two teams sharing 8 compare EQUAL and their
     * relative order is non-deterministic -- the draft then starts from an order that is
     * not merely wrong but unstable between runs.
     *
     * ── THE SHUFFLE WAS ALSO NOT A SHUFFLE ─────────────────────────────────
     *
     * `[...teams].sort(() => Math.random() - 0.5)` is the classic broken shuffle: an
     * inconsistent, non-transitive comparator does not yield a uniform permutation, and
     * the bias depends on the engine's sort implementation. That matters here more than
     * usual, because until SAB-917 re-rolling was the ONLY way to influence draft order --
     * so the fairness of the roll was the whole mechanism.
     *
     * Fisher-Yates below, matching nfl-initialize-draft:125 so the two paths agree.
     *
     * The write now goes through nfl_set_draft_order (SAB-917), which is SECURITY DEFINER
     * -- the only way a commissioner can write other teams' rows at all -- reassigns 1..N
     * in ONE statement, and asserts the result is a complete permutation before returning.
     */
    const handleRandomizeDraftOrder = async () => {
      setActionLoading('randomize');
      try {
        // Fisher-Yates, unbiased. Walk down, swap with a uniformly chosen index at or
        // below the cursor.
        const ids = teams.map(t => t.id);
        for (let i = ids.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [ids[i], ids[j]] = [ids[j], ids[i]];
        }
        const { data, error } = await (supabase as any)
          .rpc('nfl_set_draft_order', { p_league_id: currentLeague.id, p_team_ids: ids });
        if (error) throw new Error(error.message);
        // The RPC returns { ok:false, code } for expected refusals rather than throwing,
        // so a non-throwing call is NOT proof it applied. This is the check whose absence
        // let the old loop report success while landing one row.
        if (!data?.ok) {
          toast({
            title: 'Could not randomize the draft order',
            description: data?.code === 'DRAFT_STARTED'
              ? 'The draft has already started, so the order is fixed.'
              : data?.code === 'COMMISSIONER_ONLY'
                ? 'Only the commissioner can change the draft order.'
                : String(data?.code ?? 'Unknown error'),
            variant: 'destructive',
          });
          return;
        }
        await queryClient.invalidateQueries({ queryKey: ['nfl-teams', currentLeague.id] });
        toast({
          title: 'Draft Order Randomized',
          description: `${data.teams} teams reordered`,
        });
      } catch (err) {
        toast({ title: 'Error', description: String(err), variant: 'destructive' });
      } finally {
        setActionLoading(null);
      }
    };

    const handleBroadcast = async () => {
      if (!broadcastMsg.trim() || !user) return;
      setSendingBroadcast(true);
      try {
        const { error } = await supabase
          .from('nfl_chat_messages')
          .insert({
            league_id: currentLeague.id,
            user_id: user.id,
            message: broadcastMsg.trim(),
            message_type: 'commissioner_broadcast',
            metadata: { from: 'commissioner' },
          });

        if (error) {
          toast({ title: 'Error', description: error.message, variant: 'destructive' });
        } else {
          toast({ title: 'Broadcast Sent', description: 'Your message has been sent to the league' });
          setBroadcastMsg('');
        }
      } catch (err) {
        toast({ title: 'Error', description: String(err), variant: 'destructive' });
      } finally {
        setSendingBroadcast(false);
      }
    };

    const handleRemoveMember = async (memberId: string) => {
      const member = members.find(m => m.user_id === memberId);
      if (!member || member.role === 'commissioner') return;
      setRemovingMember(memberId);
      try {
        await (supabase as any)
          .from('nfl_league_members')
          .delete()
          .eq('league_id', currentLeague.id)
          .eq('user_id', memberId);

        // Remove their team too
        const team = teams.find(t => t.owner_id === memberId);
        if (team) {
          await (supabase as any)
            .from('nfl_teams')
            .delete()
            .eq('id', team.id);
        }

        toast({ title: 'Member Removed' });
        setMembers(prev => prev.filter(m => m.user_id !== memberId));
      } catch (err) {
        toast({ title: 'Error', description: String(err), variant: 'destructive' });
      } finally {
        setRemovingMember(null);
      }
    };

    // SAB-425: derive current visibility from leagueData. Falls back to
    // 'private' for leagues that pre-date the column (treats them as the
    // safe default).
    const currentVisibility: 'private' | 'public' =
      (leagueData as any)?.visibility === 'public' ? 'public' : 'private';

    return (
      <div className="space-y-4">
        <BackButton />
        <SectionTitle>Commissioner Tools</SectionTitle>

        {/* SAB-425: League visibility toggle */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              League Visibility
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {(['private', 'public'] as const).map(v => (
                <button
                  key={v}
                  onClick={() => {
                    if (v === currentVisibility) return;
                    callUpdateLeague(
                      { visibility: v },
                      v === 'public' ? 'League is now public' : 'League is now private',
                    ).then(() => setLeagueData((d: any) => d ? { ...d, visibility: v } : d));
                  }}
                  disabled={actionLoading === 'visibility'}
                  className={cn(
                    'py-3 rounded-lg text-sm font-bold border transition-all flex flex-col items-center gap-0.5',
                    currentVisibility === v
                      ? 'bg-primary/10 text-primary border-primary/30'
                      : 'bg-muted/30 text-muted-foreground border-border hover:border-primary/20'
                  )}
                >
                  <span className="capitalize">{v}</span>
                  <span className="text-[9px] font-normal opacity-80">
                    {v === 'private' ? 'Invite code only' : 'Listed on Browse'}
                  </span>
                </button>
              ))}
            </div>
            {currentVisibility === 'public' && (
              <p className="text-[10px] text-muted-foreground leading-snug">
                Anyone with open seats remaining can join without the invite code. Drops off the Browse list once full.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Broadcast */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              League Broadcast
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Input
              placeholder="Send a message to the whole league..."
              value={broadcastMsg}
              onChange={(e) => setBroadcastMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleBroadcast()}
              maxLength={500}
              className="bg-muted/30"
            />
            <Button
              size="sm"
              onClick={handleBroadcast}
              disabled={!broadcastMsg.trim() || sendingBroadcast}
              className="w-full bg-primary hover:bg-primary/90"
            >
              {sendingBroadcast ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Send Broadcast
            </Button>
          </CardContent>
        </Card>

        {/* Team Management */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              Team Management
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-0.5">
            {members.map(m => (
              <div key={m.user_id} className="flex items-center justify-between py-2 border-b border-border/30 last:border-b-0">
                <div>
                  <p className="text-sm text-foreground">{m.username || 'Unknown'}</p>
                  {m.team_name && <p className="text-[10px] text-muted-foreground">{m.team_name}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {m.role === 'commissioner' ? (
                    <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[8px]">COMMISH</Badge>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveMember(m.user_id)}
                      disabled={removingMember === m.user_id}
                      className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    >
                      {removingMember === m.user_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Score Corrections */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground flex items-center gap-2">
              <RotateCcw className="h-4 w-4 text-primary" />
              Score Corrections
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {/* SAB-691: rescoring is an operator action, not a commissioner one.
                The old button here never worked and inviting commissioners to
                rescore money-affecting weeks invites disputes. */}
            <p className="text-[10px] text-muted-foreground">
              Scores are provisional until the official stat-correction deadline after each
              week. Provider stat corrections are applied automatically; corrected matchups
              update their result with no action needed from the commissioner.
            </p>
          </CardContent>
        </Card>

        {/* SAB-790: "Season Management" card removed — all three buttons were dead.
            - "Advance to Week N" wrote current_week, which SAB-727 removed from
              nfl-update-league's ALLOWED_FIELDS. SAB-727 explicitly proposed removing this
              button too ("separately proposed for removal — dropping current_week from
              ALLOWED_FIELDS covers both"); the field was dropped and the button was not.
              The engine owns week advancement.
            - "Activate Season" / "Pause Season" wrote status='active'/'paused'. Those are
              not valid nfl_leagues statuses AT ALL — the only values in prod are
              pre_draft, in_season and completed — so they never did anything coherent even
              before SAB-727 locked status out. The engines own lifecycle transitions
              (generate-nfl-schedule -> in_season, nfl-make-pick -> draft_completed,
              matchup-engine -> completed, which fires the season_ended_at payout trigger).
            All three returned a rejection toast to the commissioner. Removed rather than
            re-plumbed: each writes a field a commissioner should not set. */}

        {/* Draft Controls */}
        <Card className="bg-card border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-foreground">Draft Controls</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 space-y-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start border-border text-foreground hover:bg-muted"
              onClick={handleRandomizeDraftOrder}
              disabled={actionLoading === 'randomize'}
            >
              {actionLoading === 'randomize' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shuffle className="h-4 w-4 mr-2 text-primary" />}
              Randomize Draft Order
            </Button>
          </CardContent>
        </Card>

        {/* SAB-727: "Reset to Week 1" removed. It wrote nfl_leagues.current_week=1,
            corrupting the week pointer the scoring/advancement/season-end system
            keys off. The engine owns week advancement.

            SAB-907 correction: this comment used to add "and current_week is no longer a
            writable field on nfl-update-league", which was TRUE of the edge function and
            FALSE of the database. ALLOWED_FIELDS guards one code path; RLS on nfl_leagues
            is USING (commissioner_id = auth.uid()) with a table-level GRANT UPDATE and no
            column list, so a commissioner could PATCH the column directly and skip the
            edge function entirely. current_week is genuinely unwritable now because
            nfl_leagues_guard_cols rejects it at the table with 42501 -- not because the
            field was dropped from an allowlist. Do not restore the old wording: a guard
            that exists in one path is not a guard. */}
      </div>
    );
  }

  return null;
}
